import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  FormSectionDto,
  UpdateFormSectionDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_FORM_SECTIONS_CONFIG } from "../domain";

import { formsSectionsConfigApi } from "@api/services";
import { ACTION_GET_FORM_SECTIONS, DOMAIN_FORMS, roleGuard } from "@api";

const ACTION_UPDATE_FORM_SECTION_CONFIG = "UPDATE_FORM_SECTION_CONFIG";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type UpdateFormSectionConfigProps = {
  sectionId: string;
  dto: UpdateFormSectionDto;
};

const updateFormSectionConfig = async (
  props: UpdateFormSectionConfigProps,
): Promise<FormSectionDto> => {
  const response =
    await formsSectionsConfigApi.formSectionsConfigControllerUpdateFormSectionConfig(
      props.sectionId,
      props.dto,
    );

  return response.data;
};

export const useUpdateFormSectionConfig = (
  user: UserProfileDto | null,
  formId: string,
): {
  updateFormSectionConfig: UseMutateFunction<
    FormSectionDto,
    Error,
    UpdateFormSectionConfigProps,
    unknown
  >;
  updateFormSectionConfigEnabled: boolean;
  isSuccessUpdateFormSectionConfig: boolean;
  isPendingUpdateFormSectionConfig: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_FORM_SECTIONS_CONFIG, ACTION_UPDATE_FORM_SECTION_CONFIG],
    mutationFn: enabled ? updateFormSectionConfig : undefined,
    onSuccess(data) {
      const queryKey = [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId];
      const currentData = queryClient.getQueryData<FormSectionDto[] | undefined>(
        queryKey,
      );

      queryClient.setQueryData(queryKey, () => {
        return currentData?.map((d) => {
          if (d.id === data.id) {
            return data;
          }

          return d;
        });
      });
    },
  });

  return {
    updateFormSectionConfig: mutate,
    updateFormSectionConfigEnabled: enabled,
    isSuccessUpdateFormSectionConfig: isSuccess,
    isPendingUpdateFormSectionConfig: isPending,
  };
};
